/**
 * Empfänger zwischen An, Cc und Bcc verschieben oder kopieren (T-21).
 *
 * Rein und ohne React: Das Verfassen-Fenster hält die drei Felder als
 * getrennte Listen; eine Verschiebung muss sie aber gemeinsam ändern
 * (aus Feld A raus, in Feld B rein, ohne Dublette). Diese Funktion ist die
 * eine Stelle dafür — Menü und Ziehen benutzen sie beide.
 */

export type EmpfaengerFeld = "to" | "cc" | "bcc";

export type EmpfaengerState = {
    to: string[];
    cc: string[];
    bcc: string[];
};

/** Steht die Adresse schon in der Liste? Groß-/Kleinschreibung zählt nicht. */
export function enthaeltAdresse(liste: readonly string[], adresse: string): boolean {
    const a = adresse.trim().toLowerCase();
    return liste.some((x) => x.trim().toLowerCase() === a);
}

/**
 * Verschiebt (oder mit `kopie` kopiert) die Adresse an `index` im Feld `von`
 * ans Ende des Felds `nach`.
 *
 * - Steht sie im Ziel schon (egal in welcher Schreibweise), kommt sie dort
 *   nicht ein zweites Mal hinein; beim Verschieben verschwindet sie trotzdem
 *   aus der Quelle.
 * - `von === nach` oder ein ungültiger Index ändern nichts — dann kommt
 *   derselbe Zustand (gleiche Referenz) zurück, damit React nicht neu rendert.
 */
export function verschiebeEmpfaenger(
    state: EmpfaengerState,
    von: EmpfaengerFeld,
    index: number,
    nach: EmpfaengerFeld,
    opts: { kopie?: boolean } = {},
): EmpfaengerState {
    if (von === nach) return state;
    const quelle = state[von];
    if (!Number.isInteger(index) || index < 0 || index >= quelle.length) return state;

    const adresse = quelle[index];
    const ziel = enthaeltAdresse(state[nach], adresse) ? state[nach] : [...state[nach], adresse];
    const neueQuelle = opts.kopie ? quelle : quelle.filter((_, i) => i !== index);

    if (ziel === state[nach] && neueQuelle === quelle) return state;
    return { ...state, [von]: neueQuelle, [nach]: ziel };
}

/**
 * T-435 — getippten oder eingefügten Text in einzelne Empfänger zerlegen.
 *
 * Outlook trennt Adressen mit Semikolon und hängt oft eins ans Ende
 * („a@x.de; b@y.de;"). Wer das gewohnt ist oder eine Liste aus Outlook
 * einfügt, bekam bisher Chips wie „sah@courte.com;" — die der Server mit 400
 * ablehnt, sodass weder Speichern noch Senden ging.
 *
 * - Trenner sind Semikolon, Komma und Zeilenumbruch.
 * - „Name <adresse>" wird zur Adresse; Anführungszeichen und spitze Klammern
 *   fallen weg.
 * - Teile ohne „@" sind keine Adressen, sondern Suchtext („Hahn, Sascha",
 *   T-25). Sie kommen als `rest` zurück und bleiben im Feld stehen.
 */
export function zerlegeEmpfaengerEingabe(text: string): { adressen: string[]; rest: string } {
    const adressen: string[] = [];
    const rest: string[] = [];
    for (const roh of String(text ?? "").split(/[;,\n\r]+/)) {
        const teil = roh.trim();
        if (!teil) continue;
        if (!teil.includes("@")) {
            rest.push(teil);
            continue;
        }
        const inKlammern = /<([^<>]*@[^<>]*)>/.exec(teil);
        const adresse = (inKlammern ? inKlammern[1] : teil)
            .replace(/^[\s"'<]+|[\s"'>.]+$/g, "")
            .trim();
        if (adresse && !enthaeltAdresse(adressen, adresse)) adressen.push(adresse);
    }
    return { adressen, rest: rest.join(", ") };
}

/**
 * Ganze Liste bereinigen (vorbefüllte Empfänger, geladene Entwürfe): jeder
 * Eintrag wird zerlegt, Dubletten fallen weg.
 */
export function bereinigeEmpfaengerListe(liste: readonly string[] | null | undefined): string[] {
    const aus: string[] = [];
    for (const eintrag of liste ?? []) {
        const { adressen, rest } = zerlegeEmpfaengerEingabe(eintrag);
        // Ein Eintrag ganz ohne „@" bleibt, wie er ist — er wird als ungültig
        // markiert, statt still zu verschwinden.
        const teile = adressen.length > 0 ? adressen : rest ? [eintrag.trim()] : [];
        for (const a of teile) if (!enthaeltAdresse(aus, a)) aus.push(a);
    }
    return aus;
}

/**
 * T-435 — ist der Chip eine Adresse, die auch der Server annimmt?
 *
 * Die Formularregel (`validateEmail`) prüft nach dem Punkt nur „zwei Zeichen
 * ohne Leerzeichen" und ließ so „sah@courte.com;" durch; der Server
 * (class-validator `isEmail`) lehnt das ab. Hier gilt deshalb: keine Trenner,
 * Klammern oder Anführungszeichen, und die Domain besteht aus Labels aus
 * Buchstaben, Ziffern und Bindestrich mit einer Endung aus Buchstaben.
 */
export function istZustellbareAdresse(adresse: string): boolean {
    const a = String(adresse ?? "").trim();
    if (!a || /[\s;,<>()[\]\\":]/.test(a)) return false;
    const at = a.lastIndexOf("@");
    if (at <= 0 || at !== a.indexOf("@")) return false;
    const lokal = a.slice(0, at);
    const domain = a.slice(at + 1);
    if (lokal.length > 64 || lokal.startsWith(".") || lokal.endsWith(".") || lokal.includes("..")) return false;
    const labels = domain.split(".");
    if (labels.length < 2) return false;
    if (!labels.every((l) => /^[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?$/u.test(l))) return false;
    return /^\p{L}{2,}$/u.test(labels[labels.length - 1]);
}
